import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { PageHeader } from '@/components/shared/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/toast';
import { useElevateSession } from '@/hooks/use-elevate-session';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS } from '@/lib/permissions';
import { useChurchStore } from '@/stores/church-store';
import { formatDate } from '@/utils/format';

import { GivingSubnav } from './giving-subnav';

interface Period {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  status: 'open' | 'closed';
}

export const PeriodsPage = () => {
  const { t } = useTranslation('giving');
  const { t: tTips } = useTranslation('tips');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const membership = useChurchStore((state) => state.membership);
  const canClosePeriod = hasPermission(membership, PERMISSIONS.GIVING_CLOSE_PERIOD);
  const { elevateAndRetry, dialog: elevateDialog } = useElevateSession();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [closeTarget, setCloseTarget] = useState<Period | null>(null);
  const [reopenTarget, setReopenTarget] = useState<Period | null>(null);

  const list = useQuery({
    queryKey: ['giving', 'periods'],
    queryFn: () => api.get<{ data: Period[] }>('/giving/periods?pageSize=100'),
  });

  const create = useMutation({
    mutationFn: () =>
      api.post<Period>('/giving/periods', {
        name,
        startDate,
        endDate,
      }),
    onSuccess: () => {
      toast.success(t('periods.saved'));
      setOpen(false);
      setName('');
      setStartDate(null);
      setEndDate(null);
      void queryClient.invalidateQueries({ queryKey: ['giving', 'periods'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const close = useMutation({
    mutationFn: (periodId: string) =>
      elevateAndRetry(() => api.post<Period>(`/giving/periods/${periodId}/close`, {}), {
        description: t('elevate.closePeriod'),
      }),
    onSuccess: () => {
      toast.success(t('periods.closed'));
      setCloseTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['giving', 'periods'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const reopen = useMutation({
    mutationFn: (periodId: string) =>
      elevateAndRetry(() => api.post<Period>(`/giving/periods/${periodId}/reopen`, {}), {
        description: t('elevate.reopenPeriod'),
      }),
    onSuccess: () => {
      toast.success(t('periods.reopened'));
      setReopenTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['giving', 'periods'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const columns: DataTableColumn<Period>[] = [
    {
      id: 'dates',
      header: t('periods.columns.dates'),
      cell: (row) => `${formatDate(row.startDate)} – ${formatDate(row.endDate)}`,
    },
    {
      id: 'name',
      header: t('periods.columns.name'),
      cell: (row) => row.name,
    },
    {
      id: 'status',
      header: t('periods.columns.status'),
      cell: (row) => (
        <Badge variant={row.status === 'open' ? 'success' : 'neutral'}>
          {row.status === 'open' ? t('periods.statusOpen') : t('periods.statusClosed')}
        </Badge>
      ),
    },
  ];

  const rows = list.data?.data ?? [];

  return (
    <div>
      <PageHeader
        title={t('periods.title')}
        tipId="giving.periods.close"
        headerActions={
          canClosePeriod ? (
            <Button onClick={() => setOpen(true)}>{t('periods.create')}</Button>
          ) : undefined
        }
      />
      <GivingSubnav />
      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        empty={<p className="text-body-sm text-fg-secondary">{t('periods.empty')}</p>}
        rowActions={
          canClosePeriod
            ? (row) =>
                row.status === 'open' ? (
                  <Button size="sm" variant="outline" onClick={() => setCloseTarget(row)}>
                    {t('periods.close')}
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => setReopenTarget(row)}>
                    {t('periods.reopen')}
                  </Button>
                )
            : undefined
        }
        actionsHeader={canClosePeriod ? tCommon('table.actions') : undefined}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent closeLabel={tCommon('action.close')}>
          <DialogHeader>
            <DialogTitle>{t('periods.create')}</DialogTitle>
          </DialogHeader>
          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              if (!startDate || !endDate) return;
              create.mutate();
            }}
          >
            <div className="flex flex-col gap-1.5">
              <Label>{t('periods.name')}</Label>
              <Input value={name} onChange={(event) => setName(event.target.value)} required />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>{t('periods.startDate')}</Label>
              <DatePicker value={startDate} onChange={setStartDate} popoverPortal="dialog" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>{t('periods.endDate')}</Label>
              <DatePicker value={endDate} onChange={setEndDate} popoverPortal="dialog" />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                {tCommon('action.cancel')}
              </Button>
              <Button type="submit" loading={create.isPending} disabled={!startDate || !endDate}>
                {t('periods.create')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={closeTarget !== null}
        onOpenChange={(next) => {
          if (!next) setCloseTarget(null);
        }}
        title={t('confirm.closePeriod.title', { name: closeTarget?.name ?? '' })}
        description={tTips('giving.periods.close')}
        confirmLabel={t('confirm.closePeriod.confirm')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        loading={close.isPending}
        typeToConfirm={
          closeTarget
            ? {
                value: closeTarget.name,
                label: tCommon('confirm.typeToConfirm', { value: closeTarget.name }),
              }
            : undefined
        }
        onConfirm={() => {
          if (closeTarget) close.mutate(closeTarget.id);
        }}
      />

      <ConfirmDialog
        open={reopenTarget !== null}
        onOpenChange={(next) => {
          if (!next) setReopenTarget(null);
        }}
        title={t('confirm.reopenPeriod.title', { name: reopenTarget?.name ?? '' })}
        description={tTips('giving.periods.reopen')}
        confirmLabel={t('confirm.reopenPeriod.confirm')}
        cancelLabel={tCommon('action.cancel')}
        loading={reopen.isPending}
        onConfirm={() => {
          if (reopenTarget) reopen.mutate(reopenTarget.id);
        }}
      />
      {elevateDialog}
    </div>
  );
};
